import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  FileText,
  Plus,
  Trash2,
  Paperclip,
  Upload,
  X,
  ScrollText,
  MoreHorizontal,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { Th, Td, openProtectedFile } from "@/components/employee/tabHelpers";

export const EMPLOYEE_DOC_TYPES = [
  { value: "id_front", label: "SZIG Eleje" },
  { value: "id_back", label: "SZIG Hátulja" },
  { value: "tax", label: "Adó" },
  { value: "taj", label: "TAJ" },
  { value: "address", label: "Lakcímkártya" },
  { value: "employment_contract", label: "Munkaszerződés" },
  { value: "nda", label: "Titoktartási szerződés" },
  { value: "gdpr", label: "Adatkezelési tájékoztató" },
];

const typeLabel = (v) =>
  EMPLOYEE_DOC_TYPES.find((t) => t.value === v)?.label || v || "—";

const ACCEPTED = ".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic";
const MAX_BYTES = 20 * 1024 * 1024;

export function EmployeeDocumentsTab({ employeeId, employee }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/documents`);
      setItems(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a dokumentumokat."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const onCreated = (doc) => {
    setItems((prev) => [doc, ...prev]);
  };

  const onDelete = async (doc) => {
    if (!window.confirm("Biztosan törlöd ezt a dokumentumot?")) return;
    try {
      await api.delete(`/employees/${employeeId}/documents/${doc._id}`);
      setItems((prev) => prev.filter((d) => d._id !== doc._id));
      setSelected(null);
      toast.success("Dokumentum törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  return (
    <section data-testid="employee-documents-section" className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <ScrollText className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
              Dokumentumok
            </span>
          </div>
          <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-[22px]">
            Munkavállaló dokumentumai
          </h2>
        </div>
        <Button
          size="sm"
          data-testid="new-employee-document-button"
          onClick={() => setOpenCreate(true)}
          className="self-start"
        >
          <Plus className="h-4 w-4" />
          Új dokumentum
        </Button>
      </header>

      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Dokumentumok
            betöltése…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="employee-documents-empty"
            className="flex flex-col items-center gap-3 px-6 py-14 text-center"
          >
            <div className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
              <ScrollText className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Nincs rögzített dokumentum</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
              Tölts fel SZIG-et, adókártyát, TAJ-t, munkaszerződést vagy
              titoktartási szerződést.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-8"
              onClick={() => setOpenCreate(true)}
            >
              <Plus className="h-3.5 w-3.5" /> Új dokumentum
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employee-documents-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Típus</Th>
                  <Th>Fájlok</Th>
                  <Th>Státusz</Th>
                  <Th>Feltöltötte</Th>
                  <Th>Időpont</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const awaiting =
                    it.awaiting_signature ||
                    (["nda", "employment_contract", "gdpr"].includes(it.doc_type) &&
                      it.generated_from_template &&
                      it.file1_url &&
                      !it.file2_url);
                  const signed =
                    ["nda", "employment_contract", "gdpr"].includes(it.doc_type) &&
                    it.generated_from_template &&
                    it.file2_url;
                  return (
                  <tr
                    key={it._id}
                    data-testid={`employee-document-row-${it._id}`}
                    onClick={() => setSelected(it)}
                    className={cn(
                      "cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]",
                      awaiting &&
                        "bg-amber-50/60 dark:bg-amber-500/[0.06] hover:bg-amber-100/60 dark:hover:bg-amber-500/[0.1]",
                    )}
                  >
                    <Td>
                      <span className="font-medium">{typeLabel(it.doc_type)}</span>
                    </Td>
                    <Td className="text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        {it.file1_url && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5">
                            <Paperclip className="h-3 w-3" /> Fájl 1
                          </span>
                        )}
                        {it.file2_url && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5">
                            <Paperclip className="h-3 w-3" /> Fájl 2
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td>
                      {awaiting ? (
                        <span
                          data-testid={`status-awaiting-${it._id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-100/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300"
                        >
                          <AlertTriangle className="h-3 w-3" /> Aláírásra vár
                        </span>
                      ) : signed ? (
                        <span
                          data-testid={`status-signed-${it._id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-300/70 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Aláírva
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground/60">—</span>
                      )}
                    </Td>
                    <Td>{it.created_by_name || "—"}</Td>
                    <Td className="text-muted-foreground">
                      {formatDateHu(it.created_at)}
                    </Td>
                    <Td
                      className="pr-4 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="ml-auto flex items-center justify-end gap-1">
                        {awaiting && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-500/10"
                            onClick={() => setSigningDoc(it)}
                            data-testid={`upload-signed-${it._id}`}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            Aláírt feltöltése
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`employee-document-actions-${it._id}`}
                              aria-label="Dokumentum műveletek"
                              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`delete-employee-document-${it._id}`}
                              onClick={() => onDelete(it)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Törlés
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateEmployeeDocumentDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        employeeId={employeeId}
        onCreated={onCreated}
      />
      <EmployeeDocumentDetailDialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        doc={selected}
        employeeId={employeeId}
        onDelete={onDelete}
      />
      <SignedUploadDialog
        open={!!signingDoc}
        onOpenChange={(v) => !v && setSigningDoc(null)}
        doc={signingDoc}
        employeeId={employeeId}
        onUploaded={(updated) => {
          setItems((prev) => prev.map((d) => (d._id === updated._id ? updated : d)));
          setSigningDoc(null);
        }}
      />
    </section>
  );
}

function CreateEmployeeDocumentDialog({
  open,
  onOpenChange,
  employeeId,
  onCreated,
}) {
  const [docType, setDocType] = useState("");
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setDocType("");
    setFile1(null);
    setFile2(null);
    setErr("");
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!docType) {
      setErr("Típus kiválasztása kötelező.");
      return;
    }
    if (!file1 && !file2) {
      setErr("Legalább egy fájl feltöltése kötelező.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      if (file1) fd.append("file1", file1);
      if (file2) fd.append("file2", file2);
      const { data } = await api.post(
        `/employees/${employeeId}/documents`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Dokumentum rögzítve.");
      onCreated(data);
      onOpenChange(false);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="employee-document-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Új dokumentum</DialogTitle>
          <DialogDescription>
            Válaszd ki a típust és tölts fel legalább egy fájlt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Típus *" testId="emp-doc-type">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="emp-doc-type-trigger">
                <SelectValue placeholder="Válassz típust…" />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYEE_DOC_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <FilePicker
            label="Fájl 1"
            testid="emp-doc-file1"
            file={file1}
            onChange={(f) => {
              setErr("");
              setFile1(f);
            }}
          />
          <FilePicker
            label="Fájl 2 (opcionális)"
            testid="emp-doc-file2"
            file={file2}
            onChange={(f) => {
              setErr("");
              setFile2(f);
            }}
          />

          {err && (
            <div
              data-testid="emp-doc-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="emp-doc-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FilePicker({ label, file, onChange, testid }) {
  const inputId = `emp-doc-${testid}`;
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`A fájl mérete meghaladja a maximumot (${MAX_BYTES / (1024 * 1024)} MB).`);
      e.target.value = "";
      return;
    }
    onChange(f);
  };
  return (
    <label className="block space-y-1.5" data-testid={testid}>
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <input
        id={inputId}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
      />
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
          <FileText
            className="h-4 w-4 shrink-0 text-muted-foreground"
            strokeWidth={1.75}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{file.name}</p>
            <p className="text-[11px] text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={() => onChange(null)}
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            aria-label="Fájl eltávolítása"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testid}-button`}
          onClick={() => document.getElementById(inputId)?.click()}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground transition-colors",
            "hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground"
          )}
        >
          <Upload className="h-4 w-4" strokeWidth={1.75} />
          Fájl kiválasztása
        </button>
      )}
    </label>
  );
}

function EmployeeDocumentDetailDialog({
  open,
  onOpenChange,
  doc,
  employeeId,
  onDelete,
}) {
  if (!doc) return null;
  const openFile = (slot, fallback) =>
    openProtectedFile(
      `/employees/${employeeId}/documents/${doc._id}/file/${slot}`,
      doc[`${slot}_content_type`],
      fallback
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="employee-document-detail-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{typeLabel(doc.doc_type)}</DialogTitle>
          <DialogDescription>
            Feltöltötte: <b className="text-foreground">{doc.created_by_name || "—"}</b>
            {doc.created_at && (
              <> · {formatDateHu(doc.created_at)}</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {doc.file1_url ? (
            <button
              type="button"
              onClick={() => openFile("file1", typeLabel(doc.doc_type))}
              data-testid="employee-document-file1-link"
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{doc.file1_name || "Fájl 1"}</p>
                <p className="text-[11px] text-muted-foreground">Fájl 1 · megnyitás</p>
              </div>
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">— Fájl 1: nincs feltöltve</p>
          )}
          {doc.file2_url ? (
            <button
              type="button"
              onClick={() => openFile("file2", typeLabel(doc.doc_type))}
              data-testid="employee-document-file2-link"
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{doc.file2_name || "Fájl 2"}</p>
                <p className="text-[11px] text-muted-foreground">Fájl 2 · megnyitás</p>
              </div>
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">— Fájl 2: nincs feltöltve</p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onDelete(doc)}
            className="text-destructive hover:text-destructive"
            data-testid="employee-document-detail-delete"
          >
            <Trash2 className="h-4 w-4" />
            Törlés
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Bezárás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function SignedUploadDialog({ open, onOpenChange, doc, employeeId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) setFile(null);
  }, [open]);

  if (!doc) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Válassz ki egy aláírt PDF/képet a feltöltéshez.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/employees/${employeeId}/documents/${doc._id}/sign`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Aláírt példány feltöltve.");
      onUploaded?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Feltöltés sikertelen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="signed-upload-dialog">
        <DialogHeader>
          <DialogTitle>Aláírt példány feltöltése</DialogTitle>
          <DialogDescription>
            {typeLabel(doc.doc_type)} — az aláírt szkennelt változatot
            tölthetsd fel (PDF/JPG/PNG, max 20 MB).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="signed-file-input"
          />
          {file ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06]"
                aria-label="Mégse"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-4 text-sm text-muted-foreground hover:border-foreground/30 hover:bg-muted/40"
              data-testid="signed-file-picker"
            >
              <Upload className="h-4 w-4" /> Fájl kiválasztása
            </button>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={uploading || !file} data-testid="signed-submit">
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Feltöltés
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
